/**
 * Validação e normalização defensiva de `LinkAutoSource`.
 * Não muta a configuração original. Retorna clone normalizado ou erro.
 * Nunca lança — JSON malformado resulta em `{ ok: false }`.
 */

import type {
  LinkAutoSource,
  LinkMatchProtocol,
  LinkQueryCondition,
  LinkTargetMatch,
  LinkValueBinding,
  LinkValueResolver,
  LinkValueTransform,
} from '@/config/shared/auto-source';
import type { SelectorConfig, SelectorInput } from '@/config/shared/selector';

import { matchPathTemplate, normalizePathname } from './LinkTargetMatcher';

export type NormalizeLinkAutoSourceResult =
  | { readonly ok: true; readonly source: LinkAutoSource }
  | { readonly ok: false; readonly reason: string };

const MAX_ORIGINS = 50;
const MAX_HOSTNAMES = 50;
const MAX_PATH_ENTRIES = 50;
const MAX_QUERY_CONDITIONS = 20;
const MAX_BINDINGS = 30;
const MAX_FROM = 10;
const MAX_ONE_OF = 50;
const MAX_NAME_LENGTH = 128;
const MAX_STRING_LENGTH = 2048;

const RESERVED_OBJECT_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const PATH_PARAM_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

const TRANSFORMS = new Set<LinkValueTransform>([
  'trim',
  'lowercase',
  'uppercase',
  'number',
]);

function fail(reason: string): NormalizeLinkAutoSourceResult {
  return { ok: false, reason };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isSafeObjectKey(key: string): boolean {
  return !RESERVED_OBJECT_KEYS.has(key);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isBoundedString(
  value: unknown,
  max = MAX_STRING_LENGTH
): value is string {
  return typeof value === 'string' && value.length <= max;
}

function isValidHostname(host: string): boolean {
  if (!host || host.length > 253) return false;
  if (host.includes('/') || host.includes(' ') || host.includes(':')) {
    return false;
  }
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i.test(
    host
  );
}

function normalizeTimerMs(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  const MAX_TIMER_MS = 60_000;
  return Math.min(value, MAX_TIMER_MS);
}

/**
 * Origin estrita: apenas protocol + host + port.
 * Rejeita pathname ≠ `/`, query, hash, username/password.
 */
function normalizeOrigin(raw: string): string | null {
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    if (url.username !== '' || url.password !== '') {
      return null;
    }
    if (url.pathname !== '/' && url.pathname !== '') {
      return null;
    }
    if (url.search !== '' || url.hash !== '') {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function cloneSelectorInput(item: unknown): SelectorInput | null {
  if (typeof item === 'string') {
    return isBoundedString(item) ? item : null;
  }
  if (!isPlainObject(item)) return null;
  if (
    !isNonEmptyString(item.type) ||
    !isNonEmptyString(item.value) ||
    !isBoundedString(item.value)
  ) {
    return null;
  }
  const type = item.type;
  if (
    type !== 'css' &&
    type !== 'id' &&
    type !== 'class' &&
    type !== 'attribute' &&
    type !== 'text'
  ) {
    return null;
  }
  return {
    type,
    value: item.value,
    ...(item.exact === true ? { exact: true } : {}),
    ...(item.caseSensitive === true ? { caseSensitive: true } : {}),
    ...(typeof item.closest === 'string' && isBoundedString(item.closest)
      ? { closest: item.closest }
      : {}),
  };
}

function cloneSelector(selector: unknown): SelectorConfig | null {
  if (typeof selector === 'string') {
    return isBoundedString(selector) ? selector : null;
  }
  if (Array.isArray(selector)) {
    if (selector.length === 0) return null;
    const items: SelectorInput[] = [];
    for (const item of selector) {
      const cloned = cloneSelectorInput(item);
      if (!cloned) return null;
      items.push(cloned);
    }
    return items;
  }
  return cloneSelectorInput(selector);
}

function validateTemplateParams(template: string): string | null {
  const parts = normalizePathname(template).split('/').filter(Boolean);
  const seen = new Set<string>();
  for (const part of parts) {
    if (!part.startsWith(':')) continue;
    const key = part.slice(1);
    if (!key) return 'path.template has empty param name';
    if (!PATH_PARAM_NAME.test(key)) {
      return `path.template param name is invalid: ${key}`;
    }
    if (!isSafeObjectKey(key)) {
      return `path.template param name is reserved: ${key}`;
    }
    if (seen.has(key)) return `path.template has duplicate param: ${key}`;
    seen.add(key);
  }
  void matchPathTemplate(template.replace(/:[^/]+/g, 'x'), template);
  return null;
}

function normalizeQueryCondition(
  condition: unknown
): LinkQueryCondition | null {
  if (!isPlainObject(condition)) return null;
  if (!isNonEmptyString(condition.key) || !isBoundedString(condition.key)) {
    return null;
  }
  const key = condition.key.trim();
  if (!isSafeObjectKey(key)) return null;

  const operator = condition.operator;
  if (operator === 'exists' || operator === 'absent') {
    return { key, operator };
  }

  if (operator === 'equals' || operator === 'starts_with') {
    if (typeof condition.value !== 'string') return null;
    if (!isBoundedString(condition.value)) return null;
    return {
      key,
      operator,
      value: condition.value,
      ...(condition.caseSensitive === true ? { caseSensitive: true } : {}),
    };
  }

  if (operator === 'one_of') {
    if (!Array.isArray(condition.value) || condition.value.length === 0) {
      return null;
    }
    if (condition.value.length > MAX_ONE_OF) return null;
    const values = condition.value.filter(
      (v): v is string => typeof v === 'string' && isBoundedString(v)
    );
    if (values.length === 0) return null;
    return {
      key,
      operator: 'one_of',
      value: [...values],
      ...(condition.caseSensitive === true ? { caseSensitive: true } : {}),
    };
  }

  return null;
}

function normalizeResolver(
  resolver: unknown,
  hasTemplate: boolean
): LinkValueResolver | null {
  if (!isPlainObject(resolver)) return null;
  const source = resolver.source;
  if (typeof source !== 'string') return null;

  switch (source) {
    case 'query':
      return isNonEmptyString(resolver.key) && isBoundedString(resolver.key)
        ? { source: 'query', key: resolver.key.trim() }
        : null;
    case 'pathParam':
      if (!hasTemplate) return null;
      if (!isNonEmptyString(resolver.key) || !isBoundedString(resolver.key)) {
        return null;
      }
      {
        const key = resolver.key.trim();
        if (!PATH_PARAM_NAME.test(key) || !isSafeObjectKey(key)) return null;
        return { source: 'pathParam', key };
      }
    case 'pathSegment':
      if (
        typeof resolver.index !== 'number' ||
        !Number.isInteger(resolver.index)
      ) {
        return null;
      }
      // Índice negativo = a partir do fim (`-1` = último segmento).
      if (resolver.index < -32 || resolver.index > 64) {
        return null;
      }
      return { source: 'pathSegment', index: resolver.index };
    case 'attribute':
      return isNonEmptyString(resolver.name) && isBoundedString(resolver.name)
        ? { source: 'attribute', name: resolver.name.trim() }
        : null;
    case 'dataset':
      return isNonEmptyString(resolver.key) && isBoundedString(resolver.key)
        ? { source: 'dataset', key: resolver.key.trim() }
        : null;
    case 'static': {
      const value = resolver.value;
      if (value === null || typeof value === 'boolean') {
        return { source: 'static', value };
      }
      if (typeof value === 'string') {
        if (!isBoundedString(value)) return null;
        return { source: 'static', value };
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return { source: 'static', value };
      }
      return null;
    }
    case 'href':
    case 'origin':
    case 'hostname':
    case 'pathname':
    case 'text':
      return { source };
    default:
      return null;
  }
}

function normalizeBinding(
  binding: unknown,
  hasTemplate: boolean
): LinkValueBinding | null {
  if (!isPlainObject(binding)) return null;
  if (binding.from == null) return null;

  const fromList = Array.isArray(binding.from) ? binding.from : [binding.from];
  if (fromList.length === 0 || fromList.length > MAX_FROM) return null;

  const normalizedFrom: LinkValueResolver[] = [];
  for (const item of fromList) {
    const resolved = normalizeResolver(item, hasTemplate);
    if (!resolved) return null;
    normalizedFrom.push(resolved);
  }

  const transform = binding.transform;
  if (
    transform !== undefined &&
    (typeof transform !== 'string' ||
      !TRANSFORMS.has(transform as LinkValueTransform))
  ) {
    return null;
  }

  return {
    from: normalizedFrom.length === 1 ? normalizedFrom[0]! : normalizedFrom,
    ...(binding.required === true ? { required: true } : {}),
    ...(typeof transform === 'string'
      ? { transform: transform as LinkValueTransform }
      : {}),
  };
}

function hasUsefulMatch(match: LinkTargetMatch): boolean {
  return Boolean(
    match.protocols?.length ||
      match.origins?.length ||
      match.hostnames?.length ||
      match.path?.exact?.length ||
      match.path?.prefixes?.length ||
      match.path?.segmentPrefixes?.length ||
      match.path?.template ||
      match.query?.conditions?.length
  );
}

function normalizeLinkAutoSourceInner(
  input: unknown
): NormalizeLinkAutoSourceResult {
  if (!isPlainObject(input)) {
    return fail('source must be an object');
  }

  if (!isNonEmptyString(input.name)) {
    return fail('name is required');
  }
  if (input.name.trim().length > MAX_NAME_LENGTH) {
    return fail('name exceeds defensive limit');
  }
  if (input.type !== 'link') {
    return fail('type must be link');
  }
  if (input.trigger !== undefined && input.trigger !== 'click') {
    return fail('trigger must be click when provided');
  }
  if (!isPlainObject(input.match)) {
    return fail('match is required');
  }

  const matchIn = input.match;
  const match: LinkTargetMatch = {};

  if (matchIn.protocols !== undefined) {
    if (!Array.isArray(matchIn.protocols) || matchIn.protocols.length === 0) {
      return fail('protocols must be a non-empty array when provided');
    }
    const protocols: LinkMatchProtocol[] = [];
    for (const p of matchIn.protocols) {
      if (p !== 'http:' && p !== 'https:') {
        return fail('protocols only allow http: and https:');
      }
      if (!protocols.includes(p)) protocols.push(p);
    }
    match.protocols = protocols;
  }

  if (matchIn.origins !== undefined) {
    if (!Array.isArray(matchIn.origins) || matchIn.origins.length === 0) {
      return fail('origins must be a non-empty array when provided');
    }
    if (matchIn.origins.length > MAX_ORIGINS) {
      return fail('origins exceeds defensive limit');
    }
    const origins: string[] = [];
    for (const raw of matchIn.origins) {
      if (!isNonEmptyString(raw) || !isBoundedString(raw)) {
        return fail('origin entries must be non-empty');
      }
      const normalized = normalizeOrigin(raw.trim());
      if (!normalized) {
        return fail(
          'origin must be protocol+host[+port] only (no path/query/hash/userinfo)'
        );
      }
      if (!origins.includes(normalized)) origins.push(normalized);
    }
    match.origins = origins;
  }

  if (matchIn.hostnames !== undefined) {
    if (!Array.isArray(matchIn.hostnames) || matchIn.hostnames.length === 0) {
      return fail('hostnames must be a non-empty array when provided');
    }
    if (matchIn.hostnames.length > MAX_HOSTNAMES) {
      return fail('hostnames exceeds defensive limit');
    }
    const hostnames: string[] = [];
    for (const raw of matchIn.hostnames) {
      if (!isNonEmptyString(raw)) {
        return fail('hostname entries must be non-empty');
      }
      const host = raw.trim().toLowerCase();
      if (!isValidHostname(host)) {
        return fail('hostname is invalid');
      }
      if (!hostnames.includes(host)) hostnames.push(host);
    }
    match.hostnames = hostnames;
  }

  if (matchIn.path !== undefined) {
    if (!isPlainObject(matchIn.path)) {
      return fail('path must be an object when provided');
    }
    const path: NonNullable<LinkTargetMatch['path']> = {};
    if (matchIn.path.exact !== undefined) {
      if (
        !Array.isArray(matchIn.path.exact) ||
        matchIn.path.exact.length === 0
      ) {
        return fail('path.exact must be non-empty when provided');
      }
      if (matchIn.path.exact.length > MAX_PATH_ENTRIES) {
        return fail('path.exact exceeds defensive limit');
      }
      path.exact = matchIn.path.exact
        .filter((p): p is string => isNonEmptyString(p) && isBoundedString(p))
        .map(p => normalizePathname(p.trim()));
      if (path.exact.length === 0) {
        return fail('path.exact has no valid entries');
      }
    }
    if (matchIn.path.prefixes !== undefined) {
      if (
        !Array.isArray(matchIn.path.prefixes) ||
        matchIn.path.prefixes.length === 0
      ) {
        return fail('path.prefixes must be non-empty when provided');
      }
      if (matchIn.path.prefixes.length > MAX_PATH_ENTRIES) {
        return fail('path.prefixes exceeds defensive limit');
      }
      path.prefixes = matchIn.path.prefixes
        .filter((p): p is string => isNonEmptyString(p) && isBoundedString(p))
        .map(p => normalizePathname(p.trim()));
      if (path.prefixes.length === 0) {
        return fail('path.prefixes has no valid entries');
      }
    }
    if (matchIn.path.segmentPrefixes !== undefined) {
      if (
        !Array.isArray(matchIn.path.segmentPrefixes) ||
        matchIn.path.segmentPrefixes.length === 0
      ) {
        return fail('path.segmentPrefixes must be non-empty when provided');
      }
      if (matchIn.path.segmentPrefixes.length > MAX_PATH_ENTRIES) {
        return fail('path.segmentPrefixes exceeds defensive limit');
      }
      path.segmentPrefixes = matchIn.path.segmentPrefixes
        .filter((p): p is string => isNonEmptyString(p) && isBoundedString(p))
        .map(p => p.trim().replace(/^\/+|\/+$/g, ''))
        .filter(Boolean);
      if (path.segmentPrefixes.length === 0) {
        return fail('path.segmentPrefixes has no valid entries');
      }
    }
    if (matchIn.path.template !== undefined) {
      if (!isNonEmptyString(matchIn.path.template)) {
        return fail('path.template must be non-empty when provided');
      }
      if (!isBoundedString(matchIn.path.template)) {
        return fail('path.template exceeds defensive limit');
      }
      const template = normalizePathname(matchIn.path.template.trim());
      const templateError = validateTemplateParams(template);
      if (templateError) return fail(templateError);
      path.template = template;
    }
    if (
      !path.exact &&
      !path.prefixes &&
      !path.segmentPrefixes &&
      !path.template
    ) {
      return fail(
        'path must define exact, prefixes, segmentPrefixes, or template'
      );
    }
    match.path = path;
  }

  if (matchIn.query !== undefined) {
    if (!isPlainObject(matchIn.query)) {
      return fail('query must be an object when provided');
    }
    if (
      !Array.isArray(matchIn.query.conditions) ||
      matchIn.query.conditions.length === 0
    ) {
      return fail('query.conditions must be a non-empty array');
    }
    if (matchIn.query.conditions.length > MAX_QUERY_CONDITIONS) {
      return fail('query.conditions exceeds defensive limit');
    }
    if (
      matchIn.query.mode !== undefined &&
      matchIn.query.mode !== 'all' &&
      matchIn.query.mode !== 'any'
    ) {
      return fail('query.mode must be all or any');
    }
    const conditions: LinkQueryCondition[] = [];
    for (const condition of matchIn.query.conditions) {
      const normalized = normalizeQueryCondition(condition);
      if (!normalized) return fail('query condition is invalid');
      conditions.push(normalized);
    }
    match.query = {
      ...(matchIn.query.mode === 'all' || matchIn.query.mode === 'any'
        ? { mode: matchIn.query.mode }
        : {}),
      conditions,
    };
  }

  if (!hasUsefulMatch(match)) {
    return fail('match must include at least one useful rule');
  }

  const hasTemplate = Boolean(match.path?.template);
  let bindings: Record<string, LinkValueBinding> | undefined;

  if (input.bindings !== undefined) {
    if (!isPlainObject(input.bindings)) {
      return fail('bindings must be an object when provided');
    }
    const keys = Object.keys(input.bindings);
    if (keys.length > MAX_BINDINGS) {
      return fail('bindings exceeds defensive limit');
    }
    bindings = Object.create(null) as Record<string, LinkValueBinding>;
    const seenKeys = new Set<string>();
    for (const key of keys) {
      if (!isNonEmptyString(key)) return fail('binding key must be non-empty');
      const trimmedKey = key.trim();
      if (!isSafeObjectKey(trimmedKey)) {
        return fail(`binding key is reserved: ${trimmedKey}`);
      }
      if (seenKeys.has(trimmedKey)) {
        return fail(`binding key collision after trim: ${trimmedKey}`);
      }
      seenKeys.add(trimmedKey);
      const binding = input.bindings[key];
      const normalized = normalizeBinding(binding, hasTemplate);
      if (!normalized) {
        if (
          !hasTemplate &&
          isPlainObject(binding) &&
          JSON.stringify(binding).includes('"pathParam"')
        ) {
          return fail('pathParam requires path.template');
        }
        return fail(`binding ${key} is invalid`);
      }
      bindings[trimmedKey] = normalized;
    }
  }

  const debounceMs = normalizeTimerMs(input.debounceMs);
  const cooldownMs = normalizeTimerMs(input.cooldownMs);

  let selector: SelectorConfig | undefined;
  if (input.selector !== undefined) {
    const cloned = cloneSelector(input.selector);
    if (!cloned) return fail('selector is invalid');
    selector = cloned;
  }

  const source: LinkAutoSource = {
    name: input.name.trim(),
    type: 'link',
    match,
    ...(input.enabled === false ? { enabled: false } : {}),
    ...(input.fireOnce === true ? { fireOnce: true } : {}),
    ...(debounceMs !== undefined ? { debounceMs } : {}),
    ...(cooldownMs !== undefined ? { cooldownMs } : {}),
    ...(input.trigger === 'click' ? { trigger: 'click' } : {}),
    ...(selector !== undefined ? { selector } : {}),
    ...(input.selectorType === 'css' ||
    input.selectorType === 'id' ||
    input.selectorType === 'class' ||
    input.selectorType === 'attribute' ||
    input.selectorType === 'text'
      ? { selectorType: input.selectorType }
      : {}),
    ...(bindings ? { bindings } : {}),
  };

  return { ok: true, source };
}

/**
 * Valida e clona uma source `link`. Nunca lança.
 */
export function normalizeLinkAutoSource(
  input: LinkAutoSource | unknown
): NormalizeLinkAutoSourceResult {
  try {
    return normalizeLinkAutoSourceInner(input);
  } catch {
    return fail('source normalization failed');
  }
}
